import { z } from "zod";

export enum DiscountReductionType {
  PERCENTAGE = "PERCENTAGE",
  MONETARY_AMOUNT = "MONETARY_AMOUNT",
}

export enum PenaltyAppliedEnum {
  IGNORE = "IGNORE",
  HAS_PENALTY_APPLIED = "HAS_PENALTY_APPLIED",
  NO_PENALTY_APPLIED = "NO_PENALTY_APPLIED",
}

export enum GuaranteeAppliedEnum {
  IGNORE = "IGNORE",
  HAS_GUARANTEE_APPLIED = "HAS_GUARANTEE_APPLIED",
  NO_GUARANTEE_APPLIED = "NO_GUARANTEE_APPLIED",
}

export enum DeltaTypeEnum {
  PERCENTAGE = "PERCENTAGE",
  MONETARY_AMOUNT = "MONETARY_AMOUNT",
}

export enum TargetTypeEnum {
  ACCOMMODATION = "ACCOMMODATION",
  MEAL_PLAN = "MEAL_PLAN",
}

export enum MealPlanUpgradeModeEnum {
  EXTRA = "EXTRA",
  INVENTORY = "INVENTORY",
}

export enum SellingLocationEnum {
  CHECKOUT = "CHECKOUT",
  NOTIFICATION = "NOTIFICATION",
  BOOKING_CONFIRMATION = "BOOKING_CONFIRMATION",
}

export enum SellingConfigurationTypeEnum {
  UPSELLING = "UPSELLING",
  CROSS_SELLING = "CROSS_SELLING",
}

export enum NotificationChannelEnum {
  EMAIL = "EMAIL",
  SMS = "SMS",
  WHATSAPP = "WHATSAPP",
}

export const dateSchema = z.preprocess(arg => {
  if (typeof arg === "string") {
    return new Date(arg);
  }
  if (arg instanceof Date) {
    return arg;
  }
}, z.date());

export const DiscountReductionTypeSchema = z.nativeEnum(DiscountReductionType);
export const PenaltyAppliedEnumSchema = z.nativeEnum(PenaltyAppliedEnum);
export const GuaranteeAppliedEnumSchema = z.nativeEnum(GuaranteeAppliedEnum);
export const DeltaTypeEnumSchema = z.nativeEnum(DeltaTypeEnum);
export const TargetTypeEnumSchema = z.nativeEnum(TargetTypeEnum);
export const MealPlanUpgradeModeEnumSchema = z.nativeEnum(MealPlanUpgradeModeEnum);
export const SellingLocationEnumSchema = z.nativeEnum(SellingLocationEnum);
export const SellingConfigurationTypeEnumSchema = z.nativeEnum(SellingConfigurationTypeEnum);
export const NotificationChannelEnumSchema = z.nativeEnum(NotificationChannelEnum);

export const MonetaryAmountSchema = z.object({
  amount: z.number(),
  currency: z.string(),
});

export const AmountDeltaSchema = z.object({
  deltaType: DeltaTypeEnumSchema,
  value: z.union([z.number(), MonetaryAmountSchema]),
});

export const DiscountAppliedSchema = z.object({
  rackRateDiscountReductionType: DiscountReductionTypeSchema,
  reduction: z.union([z.number(), MonetaryAmountSchema]),
  promoCodes: z.array(z.string()),
});

export const DateRangeSchema = z.object({
  start: dateSchema,
  end: dateSchema,
});

const baseActivationRuleSchema = {
  minStayInDays: z.number().optional(),
  maxStayInDays: z.number().optional(),
  minSourceAvailability: z.number().optional(),
  maxSourceAvailability: z.number().optional(),
  minUpgradeToAvailability: z.number().optional(),
  maxUpgradeToAvailability: z.number().optional(),
  minAmountDelta: AmountDeltaSchema.optional(),
  maxAmountDelta: AmountDeltaSchema.optional(),
  excludedDates: z.array(DateRangeSchema).optional().default([]),
  includedDates: z.array(DateRangeSchema).optional().default([]),
  penaltyApplied: PenaltyAppliedEnumSchema.optional().default(PenaltyAppliedEnum.IGNORE),
  guaranteeApplied: GuaranteeAppliedEnumSchema.optional().default(GuaranteeAppliedEnum.IGNORE),
  includedTags: z.array(z.string()).optional().default([]),
  promoCodes: z.array(z.string()).optional().default([]),
};

export const GeneralActivationRuleSchema = z.object({
  ...baseActivationRuleSchema,
  maxReleaseInDays: z.number(),
  minReleaseInDays: z.number().optional(),
});

export const TargetActivationRuleSchema = z.object({
  ...baseActivationRuleSchema,
  maxReleaseInDays: z.number().optional(),
  minReleaseInDays: z.number().optional(),
});

export const UpgradeTargetSchema = z.object({
  targetType: TargetTypeEnumSchema,
  baseAccommodationTicker: z.string().optional(),
  upgradeableToTickers: z.array(z.string()).optional(),
  baseMealPlanTicker: z.string().optional(),
  mealPlanUpgradeMode: MealPlanUpgradeModeEnumSchema.optional(),
  sellingActivationRule: TargetActivationRuleSchema.optional(),
  discountApplied: DiscountAppliedSchema.optional(),
});

export const UpgradeConfigurationSchema = z.object({
  id: z.string(),
  active: z.boolean().default(false),
  propertyTicker: z.string(),
  propertyRecipients: z.array(z.string()).optional().default([]),
  sellingLocations: z
    .array(SellingLocationEnumSchema)
    .optional()
    .default([SellingLocationEnum.NOTIFICATION]),
  notificationChannels: z
    .array(NotificationChannelEnumSchema)
    .optional()
    .default([NotificationChannelEnum.EMAIL]),
  sellingConfigurationType: SellingConfigurationTypeEnumSchema.default(
    SellingConfigurationTypeEnum.UPSELLING
  ),
  sellingTargets: z.array(UpgradeTargetSchema).optional().default([]),
  generalSellingActivationRule: GeneralActivationRuleSchema,
  discountApplied: DiscountAppliedSchema.optional(),
  createdAt: dateSchema,
  updatedAt: dateSchema,
});

export type UpgradeConfigurationDto = z.infer<typeof UpgradeConfigurationSchema>;

export type UpgradeTarget = z.infer<typeof UpgradeTargetSchema>;
export type GeneralActivationRule = z.infer<typeof GeneralActivationRuleSchema>;
export type TargetActivationRule = z.infer<typeof TargetActivationRuleSchema>;
export type AmountDelta = z.infer<typeof AmountDeltaSchema>;
export type DiscountApplied = z.infer<typeof DiscountAppliedSchema>;
export type MonetaryAmount = z.infer<typeof MonetaryAmountSchema>;
